import React, { useState, useEffect } from "react";
import InputField from "../../components/InputField";
import Switch from "../../components/Switch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfoCircle,
  faMagnifyingGlass,
} from "@fortawesome/pro-regular-svg-icons";
import ToolTip from "../ToolTip";
import Dropdown from "../Dropdown";
import { format, parseISO } from "date-fns";


const LicenseForm = ({
  license,
  setLicense,
  setHasChanges,
  disabled,
  billingGroups,
  onBillingGroupChange,
}) => {
  const [selectedBillingGroup, setSelectedBillingGroup] = useState("");

    const formatDate = (date) => {
      if (!date) return "";
      try {
        return format(parseISO(date), "yyyy-MM-dd");
      } catch (error) {
        return date;
      }
    };

  useEffect(() => {
    if (license?.billingGroupId) {
      const billingGroup = billingGroups.find((b) => {
        return b.billingGroupId == license.billingGroupId;
      });

      setSelectedBillingGroup(billingGroup.name);
    }
  }, [license, billingGroups]);

  return (
    <>
      {/* Two-column layout */}
      <div className="grid grid-cols-2 gap-6 w-full">
        {/* Column 1 */}
        <div className="flex flex-col gap-4">
          <Dropdown
            label="Billing group"
            value={selectedBillingGroup}
            options={billingGroups.map((group) => group.name)}
            onSelect={(option) => {
              const selectedGroup = billingGroups.find(
                (g) => g.name === option
              );
              if (!selectedGroup) return;

              setSelectedBillingGroup(option);

              setLicense((prev) => ({
                ...prev,
                billingGroupId: selectedGroup.billingGroupId,
                billingGroupPublicId: selectedGroup.billingGroupPublicId,
                billingGroupName: selectedGroup.name,
              }));

              setHasChanges(true);
            }}
            disabled={false}
          />

          <InputField
            label="License ID"
            placeholder="License ID"
            value={license?.licensePublicId || ""}
            onChange={(e) => {
              setLicense((s) => ({ ...s, licensePublicId: e.target?.value }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={disabled}
          />

          <InputField
            label="Created by"
            placeholder="Created by"
            value={license?.createdBy || ""}
            onChange={(e) => {
              setLicense((s) => ({ ...s, createdBy: e.target?.value }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={disabled}
          />

          <InputField
            label="Created date"
            placeholder="Created date"
            value={formatDate(license?.createdDateTimeUTC) || ""}
            onChange={(e) => {
              setLicense((s) => ({
                ...s,
                createdDateTimeUTC: e.target?.value,
              }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={disabled}
          />

          <InputField
            label="Expiration date"
            placeholder="Expiration date"
            value={formatDate(license?.expirationDate )|| ""}
            onChange={(e) => {
              setLicense((s) => ({ ...s, expirationDate: e.target?.value }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={disabled}
          />

          {/* Purchase = what Diflex pays the supplier; customer = what the customer pays and sees in DIP. */}
          <InputField
            label="Purchase unit price"
            type="number"
            placeholder="Purchase unit price"
            value={license?.unitPrice ?? ""}
            readOnly
            className="w-full"
            disabled={disabled}
          />

          <InputField
            label="Purchase price"
            type="number"
            placeholder="Purchase price"
            value={license?.price ?? ""}
            readOnly
            className="w-full"
            disabled={disabled}
          />

          <InputField
            label="Customer unit price"
            type="number"
            placeholder="Customer unit price"
            value={license?.customerUnitPrice ?? ""}
            readOnly
            className="w-full"
            disabled={disabled}
          />

          <InputField
            label="Customer price"
            type="number"
            placeholder="Customer price"
            value={license?.customerPrice ?? ""}
            readOnly
            className="w-full"
            disabled={disabled}
          />

          <InputField
            label="Updated by"
            placeholder="Updated by"
            value={license?.updatedBy || ""}
            onChange={(e) => {
              setLicense((s) => ({
                ...s,
                updatedBy: e.target?.value,
              }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={disabled}
          />
        
          <InputField
            label="Date for subscription renewal"
            placeholder="Date for subscription renewal"
            value={formatDate(license?.subscriptionRenewalDate) || ""}
            onChange={(e) => {
              setLicense((s) => ({
                ...s,
                subscriptionRenewalDate: e.target?.value,
              }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={disabled}
          />
        </div>

        {/* Column 2 */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <label className="text-sm font-bold flex space-x-2">
              <span>User principal name</span>
              <ToolTip text={"This is the email address of the user"}>
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="text-gray-400 text-md"
                />
              </ToolTip>
            </label>

            <InputField
              placeholder="User principal name"
              value={license?.userPrincipalName || ""}
              onChange={(e) => {
                setLicense((s) => ({
                  ...s,
                  userPrincipalName: e.target?.value,
                }));
                setHasChanges(true);
              }}
              readOnly
              className="w-full"
              disabled={disabled}
            />
          </div>

          <InputField
            label="Updated by"
            placeholder="Updated by"
            value={license?.updatedBy || ""}
            onChange={(e) => {
              setLicense((s) => ({ ...s, updatedBy: e.target?.value }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={disabled}
          />

          <InputField
            label="Last updated"
            placeholder="Last updated"
            value={formatDate(license?.updatedDate) || ""}
            onChange={(e) => {
              setLicense((s) => ({ ...s, updatedDate: e.target?.value }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={disabled}
          />

          <InputField
            label="Account type"
            placeholder="Account type"
            value={license?.accountTypeName || ""}
            onChange={(e) => {
              setLicense((s) => ({ ...s, accountTypeName: e.target?.value }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={disabled}
          />
          
          <InputField
            label="Updated date"
            placeholder="Updated date"
            value={formatDate(license?.updatedDateTimeUTC) || ""}
            onChange={(e) => {
              setLicense((s) => ({
                ...s,
                updatedDateTimeUTC: e.target?.value,
              }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={disabled}
          />

          <InputField
            label="Quantity used"
            placeholder="Quantity used"
            value={formatDate(license?.quantityUsed) || ""}
            onChange={(e) => {
              setLicense((s) => ({
                ...s,
                quantityUsed: e.target?.value,
              }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={disabled}
          />

          <InputField
            label="Quantity"
            placeholder="Quantity"
            value={formatDate(license?.quantity) || ""}
            onChange={(e) => {
              setLicense((s) => ({
                ...s,
                quantityUsed: e.target?.value,
              }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={disabled}
          />

          <InputField
            label="Validation period"
            placeholder="Validation period"
            value={license?.validationPeriod || ""}
            onChange={(e) => {
              setLicense((s) => ({ ...s, validationPeriod: e.target?.value }));
              setHasChanges(true);
            }}
            readOnly
            className="w-full"
            disabled={disabled}
          />

          <div className="flex flex-col w-1/2">
            <label className="text-sm font-bold pb-2">On-premises sync</label>
            <div className="flex items-center space-x-2">
              <Switch
                checked={license?.accountEnabled}
                onChange={(checked) => {
                  setLicense((s) => ({ ...s, accountEnabled: checked }));
                  setHasChanges(true);
                }}
                disabled={disabled}
              />
              <p className="text-sm">Sync</p>
            </div>
          </div>

          {license?.originalCurrency && (
            <>
              <div className="border-t pt-4 mt-2">
                <label className="text-sm font-bold text-gray-500">Currency conversion</label>
              </div>
              <InputField
                label="Original currency"
                value={license?.originalCurrency || ""}
                readOnly
                disabled={true}
              />
              <InputField
                label="Original purchase price"
                value={license?.originalPrice != null ? `${license.originalPrice} ${license.originalCurrency}` : ""}
                readOnly
                disabled={true}
              />
              <InputField
                label="Original purchase unit price"
                value={license?.originalUnitPrice != null ? `${license.originalUnitPrice} ${license.originalCurrency}` : ""}
                readOnly
                disabled={true}
              />
              <InputField
                label="Exchange rate"
                value={license?.exchangeRate != null ? `${license.exchangeRate}` : ""}
                readOnly
                disabled={true}
              />
              <InputField
                label="Exchange rate date"
                value={formatDate(license?.exchangeRateDate) || ""}
                readOnly
                disabled={true}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default LicenseForm;
